import { questionLanguageVisibility } from './contentLanguages';
import { Difficulty, QuestionType } from '@games/word-clash/shared/types';
import type { QuestionListItem } from '@games/word-clash/shared/types';

/**
 * Read-only card representation of a question for the Questions list "card"
 * view (#451). Mirrors the in-game question preview: the prompt is the hero,
 * with metadata pills and answer counts below. Clicking the card opens the
 * full editor — same affordance as clicking a table row.
 *
 * Presentational only: the parent List owns data fetching, the view toggle,
 * and navigation. This keeps the card trivially testable.
 */

export interface QuestionCardProps {
  question: QuestionListItem;
  onOpen: (question: QuestionListItem) => void;
}

function TypePill({ type }: { type: QuestionType }) {
  return (
    <span className="inline-block text-xs px-2 py-0.5 rounded uppercase tracking-wide bg-slate-100 text-slate-700">
      {type}
    </span>
  );
}

function DifficultyPill({ difficulty }: { difficulty: Difficulty }) {
  const palette =
    difficulty === Difficulty.Easy
      ? 'bg-emerald-100 text-emerald-800'
      : difficulty === Difficulty.Medium
        ? 'bg-amber-100 text-amber-800'
        : 'bg-red-100 text-red-800';
  return (
    <span
      className={`inline-block text-xs px-2 py-0.5 rounded uppercase tracking-wide ${palette}`}
    >
      {difficulty}
    </span>
  );
}

/** A compact `label: n` answer-count chip. */
function CountChip({ label, value }: { label: string; value: number }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-slate-50 border border-slate-200 px-2 py-0.5 text-xs text-slate-600">
      <span>{label}</span>
      <span className="tabular-nums font-medium text-slate-800">{value}</span>
    </span>
  );
}

export default function QuestionCard({ question: q, onOpen }: QuestionCardProps) {
  // An en-only / tr-only question is never served in the other language, so
  // that language's prompt + answers are dead data. Showing them anyway is
  // what made "is the Turkish content missing, or deliberately absent?"
  // unanswerable at a glance. See `contentLanguages.ts`.
  const { showEnglish, showTurkish } = questionLanguageVisibility(q.contentFlags);
  return (
    <button
      type="button"
      onClick={() => onOpen(q)}
      data-testid={`question-card-${q.id}`}
      className="text-left rounded-lg border border-slate-200 bg-white p-4 shadow-sm transition-colors hover:border-slate-300 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-400 flex flex-col gap-3"
    >
      {/* Metadata row. */}
      <div className="flex flex-wrap items-center gap-2">
        <TypePill type={q.type} />
        <DifficultyPill difficulty={q.difficulty} />
        <span className="text-xs text-slate-500 font-mono">{q.category}</span>
        <span
          className={
            'ml-auto inline-block text-xs px-2 py-0.5 rounded uppercase tracking-wide ' +
            (q.isActive
              ? 'bg-emerald-100 text-emerald-800'
              : 'bg-slate-200 text-slate-600')
          }
          data-testid={`card-active-${q.id}`}
        >
          {q.isActive ? 'Active' : 'Inactive'}
        </span>
      </div>

      {/* Prompt — the hero, like the in-game preview. */}
      <div className="space-y-1">
        {showEnglish && (
          <p
            className="text-base font-semibold text-slate-900 leading-snug"
            data-testid={`card-prompt-en-${q.id}`}
          >
            {q.promptEn}
          </p>
        )}
        {showTurkish && (
          <p
            className={
              showEnglish
                ? 'text-sm text-slate-600 leading-snug'
                : 'text-base font-semibold text-slate-900 leading-snug'
            }
            data-testid={`card-prompt-tr-${q.id}`}
          >
            {q.promptTr || <span className="text-slate-400">— no TR prompt —</span>}
          </p>
        )}
      </div>

      {/* At-a-glance counts. */}
      <div className="flex flex-wrap gap-1.5">
        <CountChip
          label="Aliases"
          value={
            (showEnglish ? Object.keys(q.answerAliases).length : 0) +
            (showTurkish ? Object.keys(q.answerAliasesTr).length : 0)
          }
        />
        <CountChip
          label="Wrong"
          value={
            (showEnglish ? q.plausibleWrongAnswers.length : 0) +
            (showTurkish ? q.plausibleWrongAnswersTr.length : 0)
          }
        />
      </div>

      {/* Answer lists — same content the Question Review card shows in its
          view mode, so reviewers can scan the full answer set without opening
          the editor. */}
      <dl className="space-y-2 text-sm">
        {showEnglish && (
          <AnswerList
            label="Valid answers (EN)"
            values={q.validAnswers}
            testId={`card-answers-en-${q.id}`}
          />
        )}
        {showTurkish && (
          <AnswerList
            label="Valid answers (TR)"
            values={q.validAnswersTr}
            testId={`card-answers-tr-${q.id}`}
          />
        )}
        {showEnglish && q.specialAnswers.length > 0 && (
          <AnswerList
            label="Special answers (EN)"
            values={q.specialAnswers}
            testId={`card-special-en-${q.id}`}
          />
        )}
        {showTurkish && q.specialAnswersTr.length > 0 && (
          <AnswerList
            label="Special answers (TR)"
            values={q.specialAnswersTr}
            testId={`card-special-tr-${q.id}`}
          />
        )}
      </dl>
    </button>
  );
}

/**
 * A labelled, comma-joined answer list. Mirrors the Question Review card's
 * view-mode rows: the count is in the label, the values render inline, and an
 * empty list shows an em-dash placeholder.
 */
function AnswerList({
  label,
  values,
  testId,
}: {
  label: string;
  values: readonly string[];
  testId: string;
}) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
        {label} ({values.length})
      </dt>
      <dd className="mt-0.5 text-slate-700" data-testid={testId}>
        {values.length > 0 ? (
          values.join(', ')
        ) : (
          <span className="text-slate-400">—</span>
        )}
      </dd>
    </div>
  );
}
